import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import PortalSwitcher from './PortalSwitcher';

const Header = () => {
  const location = useLocation();
  const { userProfile, currentPortal } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [selectedDateRange, setSelectedDateRange] = useState('Last 7 Days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const userDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  const useNotifications = () => {
    const [notifications, setNotifications] = useState([
      {
        id: 1,
        title: 'High-value transaction detected',
        message: 'Transaction of $15,000 detected from Merchant XYZ',
        severity: 'high',
        status: 'unread',
        createdAt: new Date()?.toISOString()
      }
    ]);
    const [unreadCount, setUnreadCount] = useState(1);

    const markAsRead = (id) => {
      setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, status: 'read' } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
      setNotifications(prev => prev?.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
    };

    return { notifications, unreadCount, markAsRead, markAllAsRead };
  };

  // Call the hook once at the top level
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const navigationItems = [
    {
      section: 'Overview',
      items: [
        { label: 'Transaction Overview', path: '/transaction-overview', icon: 'Activity', roles: ['admin', 'analyst', 'merchant'] }
      ]
    },
    {
      section: 'Merchants',
      items: [
        { label: 'Merchant Listing', path: '/merchants', icon: 'Store', roles: ['admin', 'analyst'] },
        { label: 'Merchant Analytics', path: '/merchant-analytics', icon: 'TrendingUp', roles: ['admin', 'analyst'] }
      ]
    },
    {
      section: 'Analytics',
      items: [
        { label: 'Payment Methods', path: '/payment-method-performance', icon: 'CreditCard', roles: ['admin', 'analyst', 'merchant'] }
      ]
    },
    {
      section: 'Operations',
      items: [
        { label: 'Fraud Detection', path: '/fraud-detection-center', icon: 'Shield', roles: ['admin', 'analyst'] },
        { label: 'Reconciliation', path: '/financial-reconciliation', icon: 'FileText', roles: ['admin', 'analyst', 'merchant'] }
      ]
    },
    {
      section: 'Financial',
      items: [
        { name: 'Financial Reconciliation', path: '/financial-reconciliation', icon: 'DollarSign' },
        { name: 'Automated Settlement Reconciliation', path: '/automated-settlement-reconciliation-center', icon: 'Zap' },
        { name: 'Comprehensive Reports Center', path: '/comprehensive-reports-center', icon: 'FileBarChart', roles: ['admin', 'analyst'] },
      ]
    },
    {
      section: 'Notifications',
      items: [
        { label: 'Notification Center', path: '/notification-center', icon: 'Bell', roles: ['admin', 'analyst', 'merchant'] },
        { label: 'Preferences', path: '/notification-preferences-management', icon: 'Settings', roles: ['admin', 'analyst', 'merchant'] }
      ]
    },
    {
      section: 'Administration',
      items: [
        { label: 'User Management', path: '/user-management-dashboard', icon: 'Users', roles: ['admin'] },
        { label: 'Roles & Permissions', path: '/role-permissions-management', icon: 'Key', roles: ['admin'] },
        { label: 'Activity Audit Log', path: '/user-activity-audit-log', icon: 'FileSearch', roles: ['admin'] },
        { label: 'Gateways', path: '/payment-gateway-configuration', icon: 'Layers', roles: ['admin'] }
      ]
    }
  ];

  const dateRangePresets = [
    'Today',
    'Yesterday',
    'Last 7 Days',
    'Last 30 Days',
    'This Month',
    'Last Month'
  ];

  const userInfo = {
    name: userProfile?.fullName || userProfile?.email || 'User',
    role: userProfile?.role || 'merchant',
    initials: userProfile?.fullName 
      ? userProfile?.fullName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)
      : (userProfile?.email?.[0] || 'U')?.toUpperCase()
  };

  // Filter navigation items based on current portal and user role
  const getFilteredNavigationItems = () => {
    if (currentPortal === 'merchant') {
      // Merchant portal: show only merchant-accessible items
      return navigationItems?.map(section => ({
        ...section,
        items: section?.items?.filter(item => item?.roles?.includes('merchant'))
      }))?.filter(section => section?.items?.length > 0);
    } else {
      // Admin portal: show items based on user role
      const userRole = userProfile?.role || 'merchant';
      return navigationItems?.map(section => ({
        ...section,
        items: section?.items?.filter(item => item?.roles?.includes(userRole))
      }))?.filter(section => section?.items?.length > 0);
    }
  };

  const filteredNavigationItems = getFilteredNavigationItems();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef?.current && !userDropdownRef?.current?.contains(event?.target)) {
        setUserDropdownOpen(false);
      }
      if (dateRangeRef?.current && !dateRangeRef?.current?.contains(event?.target)) {
        setDateRangeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const simulateConnection = () => {
      const statuses = ['connected', 'connecting', 'connected'];
      let index = 0;
      const interval = setInterval(() => {
        setConnectionStatus(statuses?.[index % statuses?.length]);
        index++;
      }, 5000);
      return interval;
    };

    const interval = simulateConnection();
    return () => clearInterval(interval);
  }, []);

  const handleDateRangeSelect = (preset) => {
    setSelectedDateRange(preset);
    if (preset !== 'Custom') {
      setDateRangeOpen(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setSelectedDateRange('Custom Range');
      setDateRangeOpen(false);
    }
  };

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  };

  return (
    <>
      <nav className="nav-container">
        <div className="nav-content">
          <Link to="/transaction-overview" className="nav-brand">
            <div className="nav-logo">
              <Icon name="Wallet" size={24} color="currentColor" />
            </div>
            <span className="nav-title hidden sm:block">PaymentGateway</span>
          </Link>

          <div className="nav-menu">
            {filteredNavigationItems?.map((section, sectionIndex) => (
              <React.Fragment key={section?.section}>
                <div className="nav-section">
                  {section?.items?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`nav-link ${isActiveRoute(item?.path) ? 'active' : ''}`}
                    >
                      {item?.label}
                    </Link>
                  ))}
                </div>
                {sectionIndex < filteredNavigationItems?.length - 1 && (
                  <div className="nav-section-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="nav-actions">
            <div className="flex items-center gap-3 md:gap-4">
              {/* Notification Bell */}
              <div className="relative" ref={notificationDropdownRef}>
                <button
                  onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                  className="relative p-2 rounded-lg hover:bg-muted transition-colors"
                  aria-label="Notifications"
                >
                  <Icon name="Bell" size={20} className="text-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {notificationDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-80 md:w-96 bg-popover border border-border rounded-xl shadow-lg z-1100 max-h-96 overflow-hidden flex flex-col">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-popover-foreground">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={() => {
                            markAllAsRead();
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {notifications?.slice(0, 5)?.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Icon name="Bell" size={32} className="text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">No notifications</p>
                        </div>
                      ) : (
                        notifications?.slice(0, 5)?.map((notification) => {
                          const getSeverityColor = (severity) => {
                            const colors = {
                              critical: 'text-error',
                              high: 'text-warning',
                              medium: 'text-accent',
                              low: 'text-info',
                              info: 'text-secondary',
                            };
                            return colors?.[severity] || 'text-muted-foreground';
                          };

                          return (
                            <div
                              key={notification?.id}
                              className={`px-4 py-3 border-b border-border hover:bg-muted/50 cursor-pointer transition-colors ${
                                notification?.status === 'unread' ? 'bg-primary/5' : ''
                              }`}
                              onClick={() => {
                                markAsRead(notification?.id);
                                setNotificationDropdownOpen(false);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-medium text-popover-foreground truncate">
                                      {notification?.title}
                                    </p>
                                    {notification?.status === 'unread' && (
                                      <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground line-clamp-2 mb-1">
                                    {notification?.message}
                                  </p>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs font-medium ${getSeverityColor(notification?.severity)}`}>
                                      {notification?.severity?.toUpperCase()}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {format(new Date(notification?.createdAt), 'MMM d, h:mm a')}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="px-4 py-3 border-t border-border">
                      <Link
                        to="/notification-center"
                        className="text-sm text-primary hover:underline font-medium block text-center"
                        onClick={() => setNotificationDropdownOpen(false)}
                      >
                        View all notifications
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Date Range Selector */}
              <div className="hidden md:flex items-center gap-4">
                <PortalSwitcher />
                <div className="connection-status">
                  <div className={`connection-dot ${connectionStatus}`} />
                  <span className="connection-text">{getConnectionStatusText()}</span>
                </div>

                <div className="relative" ref={dateRangeRef}>
                  <button
                    onClick={() => setDateRangeOpen(!dateRangeOpen)}
                    className="date-range-selector"
                  >
                    <Icon name="Calendar" size={16} />
                    <span className="date-range-text">{selectedDateRange}</span>
                    <Icon name="ChevronDown" size={16} />
                  </button>

                  {dateRangeOpen && (
                    <div className="date-range-dropdown">
                      <div className="date-range-presets">
                        {dateRangePresets?.map((preset) => (
                          <button
                            key={preset}
                            onClick={() => handleDateRangeSelect(preset)}
                            className={`date-range-preset ${selectedDateRange === preset ? 'active' : ''}`}
                          >
                            {preset}
                          </button>
                        ))}
                      </div>
                      <div className="date-range-custom">
                        <div className="date-range-label">Custom Range</div>
                        <div className="space-y-3">
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e?.target?.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e?.target?.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                          <button
                            onClick={handleCustomDateApply}
                            disabled={!customStartDate || !customEndDate}
                            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="user-panel" ref={userDropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="user-trigger"
                >
                  <div className="user-avatar">{userInfo?.initials}</div>
                  <div className="user-info">
                    <div className="user-name">{userInfo?.name}</div>
                    <div className="user-role">{userInfo?.role}</div>
                  </div>
                  <Icon name="ChevronDown" size={16} className="hidden md:block" />
                </button>

                {userDropdownOpen && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-item">
                      <Icon name="User" size={18} />
                      <span>Profile</span>
                    </div>
                    <div className="user-dropdown-item">
                      <Icon name="Settings" size={18} />
                      <span>Settings</span>
                    </div>
                    <div className="user-dropdown-divider" />
                    <div className="user-dropdown-item">
                      <Icon name="LogOut" size={18} />
                      <span>Logout</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="mobile-menu-button"
        aria-label="Toggle mobile menu"
      >
        <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
      <div className={`mobile-menu ${!mobileMenuOpen ? 'closed' : ''}`}>
        <div className="mobile-menu-content">
          {filteredNavigationItems?.map((section) => (
            <div key={section?.section} className="mobile-menu-section">
              <div className="mobile-menu-section-title">{section?.section}</div>
              {section?.items?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`mobile-menu-link ${isActiveRoute(item?.path) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={20} className="inline-block mr-3" />
                  {item?.label}
                </Link>
              ))}
            </div>
          ))}

          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">System</div>
            <div className="connection-status mb-4">
              <div className={`connection-dot ${connectionStatus}`} />
              <span className="connection-text">{getConnectionStatusText()}</span>
            </div>
            <button
              onClick={() => {
                setDateRangeOpen(true);
                setMobileMenuOpen(false);
              }}
              className="date-range-selector w-full justify-between"
            >
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} />
                <span className="date-range-text">{selectedDateRange}</span>
              </div>
              <Icon name="ChevronDown" size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;